// @ts-check
import React from 'react';
import removeEmptyKeys from 'utils/removeEmptyKeys';
import MatchingResult from './MatchingResult';
import { PageTitle } from 'shared/containers/portal_layout';
import LocationModal from './LocationModal';
import ProviderModal from './ProviderModal';
import PatientChooseModal from './PatientChooseModal';
import OutOfGroupModal from './OutOfGroupModal';
import ResultControls from './ResultControls';
import ModalRouter from 'shared/containers/ModalRouter';
import { showModal } from 'shared/actions/ModalActions';
import LoadingContent from 'shared/components/LoadingContent';
import LoadMoreButton from './LoadMoreButton';
import ReferralDashboardManager from './ReferralDashboardManager';
/* eslint-disable no-unused-vars */
import {
  ReferralConfirmationUrlParams,
  ReferralProfile,
  ProviderMappingResult,
  Location,
  AppointmentSlot,
} from './types';
/* eslint-enable no-unused-vars */
import styles from './Show.module.scss';
import NetworkLogo from 'shared/components/NetworkLogo';

const modals = [
  { name: 'location', component: LocationModal },
  { name: 'provider', component: ProviderModal },
  { name: 'patientChoose', component: PatientChooseModal },
  { name: 'outOfGroup', component: OutOfGroupModal },
];

/**
 * @param {ReferralConfirmationUrlParams} params
 * @returns {{pathname: string, query: JsonGETParams}}
 */
function referralConfirmationUrl(params) {
  const {
    webpageBase,
    referralProfileId,
    specialtyId,
    appointmentSlotId,
    providerMappingId,
    locationId,
    organizationId,
  } = params;
  return {
    pathname: `${webpageBase}/referral_profiles/${referralProfileId}/referral_confirmation`,
    query: removeEmptyKeys({
      appointmentSlotId,
      providerMappingId,
      locationId,
      organizationId,
      specialtyId,
    }),
  };
}

const Show = ({ match, history, location }) => {
  return (
    <div>
      <PageTitle title="New Referral: Select Provider" />
      <ReferralDashboardManager
        referralConfirmationUrl={referralConfirmationUrl}
        referralProfileId={match.params.referralProfileId}
        requestedNetworkId={location.query.network_id}
        history={history}
        render={({
          onSlotClick,
          onProviderClick,
          onLoadMore,
          onLocationClick,
          onOrganizationClick,
          onPatientChooseSubmit,
          onOutOfGroupReasonSubmit,
          referralProfile,
          results,
          currentPage,
          totalPages,
          loaded,
          isFetching,
          referrableSpecialties,
          filterByDistance,
          network,
          organizationReferralRejected
        }) =>
          !loaded ? (
            <LoadingContent />
          ) : (
            <div>
              {/* <pre>{JSON.stringify(network, null, 2)}</pre> */}
              {/* If can refer to the "Branded Portal" aka "Network" Organization, show that here: */}
              {network && network.acceptOrganizationReferrals && !organizationReferralRejected ? (
                <div className={styles.box} style={{ padding: 0 }}>
                  <div className={styles.option_set}>
                    <div className={styles.section_header_label}>
                      <p>Refer to {network.name}:</p>
                    </div>
                    <div className={styles.organization_referral_section}>
                      <div className={styles.organization_card}>
                        <div className={styles.organization_card__top}>
                          <img src={network.logoUrl} alt="logo" />
                        </div>
                        <div className={styles.organization_card__bottom}>
                          <button
                            className={styles.button}
                            onClick={() => onOrganizationClick(network.id)}
                          >
                            Refer to Organization
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* If branded portal org is present, accepts org referrals, _and_ normal referrals, show an "OR" divider: */}
              {network &&
              network.acceptOrganizationReferrals &&
              !organizationReferralRejected &&
              !network.onlyAcceptOrganizationReferrals ? (
                <h1 className={styles.section_divider}>OR</h1>
              ) : null}

              {/* If we ONLY accept organization referrals, and there is a "network", but it's rejected, we need to display the rejected message */}
              {
                network &&
                network.acceptOrganizationReferrals &&
                organizationReferralRejected &&
                network.onlyAcceptOrganizationReferrals ? (
                  <div className="px-6 py-8 text-lg text-center bg-white border rounded-lg">
                    <p className="py-4">
                      There are no results matching your search based
                      one or more of the following criteria:
                    </p>
                    <ul style={{ listStyle: 'inside' }}>
                      <li className="py-1">Search Term</li>
                      <li className="py-1">Insurance</li>
                      <li className="py-1">Location/Distance</li>
                      <li className="py-1">Specialty</li>
                      <li className="py-1">Response to Questions</li>
                    </ul>
                  </div>
                ) : null
              }

              {/* If "normal" referrals are accepted, show those results/options: */}
              {!(network && network.onlyAcceptOrganizationReferrals) ? (
                <div className={styles.box} style={{ padding: 0 }}>
                  <div className={styles.option_set}>
                    {network && network.acceptOrganizationReferrals ? (
                      <div className={styles.section_header_label}>
                        <p>Refer to a Specific Provider or Location:</p>
                      </div>
                    ) : null}
                    {network &&
                    network.id !== 'preferral' &&
                    !network.acceptOrganizationReferrals ? (
                      <div className={styles.branded_portal_logo_container}>
                        <NetworkLogo network={network} />
                      </div>
                    ) : null}
                    {network && !network.hideResultControls ? (
                      <div className={styles.result_controls_container}>
                        <ResultControls
                          className={styles.result_controls}
                          showSpecialtySelect
                          showSendUnassignedButton={
                            !network || network.id === 'preferral'
                          }
                          referrableSpecialties={referrableSpecialties}
                          filterByDistance={filterByDistance}
                          referralProfile={referralProfile}
                          openPatientChooseModal={() =>
                            showModal({
                              name: 'patientChoose',
                              referral: referralProfile.referral,
                            })
                          }
                          isFetching={isFetching}
                        />
                      </div>
                    ) : null}

                    <div style={{ width: '100%', textAlign: 'center' }}>
                      <div style={{ display: 'inline-block' }}>
                        {results.length > 0 ? (
                          <div>
                            <div
                              className={`_ResultsContainer flex flex-wrap justify-center w-full pt-4 pb-12 ${
                                network.displayResultsVertically
                                  ? 'flex-col'
                                  : ''
                              }`}
                            >
                              {results.map((r) => (
                                <MatchingResult
                                  key={r.id}
                                  matchingResult={r}
                                  referralProfile={referralProfile}
                                  onSlotClick={onSlotClick}
                                  onProviderClick={onProviderClick}
                                  onOrganizationClick={onOrganizationClick}
                                />
                              ))}
                            </div>
                            {totalPages > currentPage ? (
                              <div
                                style={{
                                  margin: '20px 40px',
                                  textAlign: 'center',
                                }}
                              >
                                <LoadMoreButton
                                  disabled={isFetching}
                                  onClick={onLoadMore}
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p>
                            {
                              "I'm sorry, there are no results matching your request"
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <ModalRouter
                modals={modals}
                onSlotClick={onSlotClick}
                onProviderClick={onProviderClick}
                onLocationClick={onLocationClick}
                onPatientChooseSubmit={onPatientChooseSubmit}
                onOutOfGroupReasonSubmit={onOutOfGroupReasonSubmit}
              />
            </div>
          )
        }
      />
    </div>
  );
};

export default Show;
